import type { ModuleId } from "./definitions.js";
import type { NPC_ARCHETYPES } from "./npcs.js";
import { relocatePatrolGameplayPoint } from "./contract-geography.js";
import type { EquipmentKey } from "./arsenal.js";
import { equipmentTierFactor, type EquipmentTier } from "./equipment-tiers.js";
import {
  ADVANCED_CONTRACTS,
  type ContractDifficulty,
  type ReputationAward,
  type SurveySiteId,
} from "./progression.js";
import {
  contractTermsForTemplate,
  originalContractTemplate,
  type ContractTerms,
} from "./contract-orders.js";
import type { EscortMissionView, EscortProgress } from "./escort-contracts.js";
export const CONTRACT_IDS = [
  "intro",
  "hunt",
  "salvage",
  "signal",
  "escort",
] as const;
export const SELECTABLE_CONTRACT_IDS = [
  "hunt",
  "salvage",
  "signal",
  "escort",
] as const;
export type ContractId = (typeof CONTRACT_IDS)[number];
export type SelectableContractId = (typeof SELECTABLE_CONTRACT_IDS)[number];
export interface ContractSelection {
  orderId: string;
  contractId: SelectableContractId;
  difficulty: ContractDifficulty;
}
import type { SectorId } from "./world-layout.js";
export { SECTOR_IDS, REGIONS, GATES, REGION_OBSTACLES, WORLD_LAYOUT } from "./world-layout.js";
export type { SectorId, RegionDefinition, GateDefinition } from "./world-layout.js";
export type MissionStage =
  "outbound" | "engage" | "collect" | "return" | "settled";
export type MissionOutcome =
  "active" | "success" | "partial" | "diverted" | "failed";
export type RescueStatus = "none" | "active" | "saved" | "lost";
export type MissionVariant = "standard" | "ambush" | "reinforced";
export type SectorWeather = "clear" | "ion-storm" | "debris-field";
export type NpcArchetype = (typeof NPC_ARCHETYPES)[number];
export const PASSIVE_IDS = [
  "none",
  "targeting",
  "capacitor",
  "repair-drone",
  "thrusters",
] as const;
export type PassiveId = (typeof PASSIVE_IDS)[number];

export interface MissionObjective {
  name: string;
  x: number;
  y: number;
  targetId?: string;
  kind: "travel" | "combat" | "collect" | "survey" | "return" | "complete";
}
export interface RunContractState {
  orderId: string;
  terms: ContractTerms;
  killedArchetypes: NpcArchetype[];
  runId: string;
  contractId: ContractId;
  sectorId: SectorId;
  variant: MissionVariant;
  stage: MissionStage;
  progress: number;
  goal: number;
  cargoHeld: number;
  outcome: MissionOutcome;
  rescue: RescueStatus;
  difficulty: ContractDifficulty;
  eliteKills: number;
  surveyedSites: SurveySiteId[];
  escort: EscortProgress | null;
}
export interface MissionView extends RunContractState {
  title: string;
  objective: MissionObjective;
  weather: SectorWeather;
  rewardCredits: string;
  rewardComponents: number;
  rewardUnlock: PassiveId;
  discoveredLoot: DiscoveredLoot[];
  escortView: EscortMissionView | null;
}
export interface DiscoveredLoot {
  id: string;
  kind: "salvage";
  x: number;
  y: number;
  value: number;
  pending: boolean;
}
export interface ContractResult {
  orderId: string;
  terms: ContractTerms;
  runId: string;
  contractId: ContractId;
  outcome: Exclude<MissionOutcome, "active">;
  credits: string;
  components: number;
  equipmentUnlock: PassiveId;
  moduleUnlock: ModuleId | null;
  blueprintUnlocks: EquipmentKey[];
  difficulty: ContractDifficulty;
  reputationAward: ReputationAward | null;
}
export interface RunResult {
  runId: string;
  credits: string;
  components: number;
  cargoCredits: string;
  cargoComponents: number;
  contracts: ContractResult[];
}
export interface ContractDefinition {
  id: ContractId;
  title: string;
  description: string;
  sectorId: SectorId;
  goal: number;
  cargoKind: "energy" | "salvage" | null;
  credits: string;
  components: number;
  unlock: PassiveId;
  objective: MissionObjective;
}

export const CONTRACTS: Record<ContractId, ContractDefinition> = {
  intro: {
    id: "intro",
    title: "Помощь на подлёте",
    description:
      "Освой движение, оружие и барьер. Помоги повреждённому транспорту и вернись за первым модулем.",
    sectorId: "wrecks",
    goal: 5,
    cargoKind: null,
    credits: "0",
    components: 0,
    unlock: "none",
    objective: {
      name: "Помоги транспорту на подлёте",
      ...relocatePatrolGameplayPoint({ x: 1260, y: 1820 }),
      kind: "travel",
    },
  },
  hunt: {
    id: "hunt",
    title: "Разомкнуть кольцо",
    description:
      "Уничтожьте три боевых корабля в поле обломков. Командир — дополнительная опасная цель.",
    sectorId: "wrecks",
    goal: 3,
    cargoKind: null,
    credits: "140",
    components: 3,
    unlock: "targeting",
    objective: {
      name: "Уничтожь три корабля патруля",
      ...relocatePatrolGameplayPoint({ x: 2140, y: 1050 }),
      kind: "combat",
    },
  },
  salvage: {
    id: "salvage",
    title: "Тихие обломки",
    description:
      "PvE-пограничье: найдите два контейнера среди мин. При гибели теряется четверть груза; ионная буря мешает щиту.",
    sectorId: "wrecks",
    goal: 2,
    cargoKind: "salvage",
    credits: "80",
    components: 2,
    unlock: "capacitor",
    objective: {
      name: "Найди два контейнера оборудования",
      x: 4990,
      y: 1160,
      kind: "collect",
    },
  },
  signal: {
    id: "signal",
    title: "Последний сигнал",
    description:
      "Спорная зона: открытый PvP, энергия и атакованный транспорт. При гибели выпадает половина обычного груза и вся контрабанда.",
    sectorId: "frontier",
    goal: 2,
    cargoKind: "energy",
    credits: "0",
    components: 0,
    unlock: "repair-drone",
    objective: {
      name: "Добудь два энергоконтейнера",
      x: 7900,
      y: 1160,
      kind: "collect",
    },
  },
  escort: {
    id: "escort",
    title: "Сопровождение транспорта",
    description:
      "Проведи транспорт по выбранному PvE-маршруту через три засады и вернись на станцию.",
    sectorId: "wrecks",
    goal: 1,
    cargoKind: null,
    credits: "150",
    components: 3,
    unlock: "thrusters",
    objective: {
      name: "Встреть транспорт на грузовом коридоре",
      ...relocatePatrolGameplayPoint({ x: 1320, y: 1050 }),
      kind: "travel",
    },
  },
};

export function missionVariant(runId: string): MissionVariant {
  let seed = 0;
  for (const character of runId)
    seed = (seed * 31 + character.charCodeAt(0)) >>> 0;
  const choice = seed % 3;
  return choice === 0 ? "standard" : choice === 1 ? "ambush" : "reinforced";
}
export function contractDefinition(
  contractId: ContractId,
  difficulty: ContractDifficulty,
): ContractDefinition {
  const base = CONTRACTS[contractId];
  return difficulty === "advanced" && contractId !== "intro"
    ? { ...base, ...ADVANCED_CONTRACTS[contractId] }
    : base;
}
export function createRunContract(
  runId: string,
  contractId: ContractId,
  difficulty: ContractDifficulty = "standard",
): RunContractState {
  const actualDifficulty = contractId === "intro" ? "standard" : difficulty;
  const definition = contractTermsForTemplate(
    originalContractTemplate(contractId),
    actualDifficulty,
  );
  return {
    orderId: `${runId}:fixture:${contractId}`,
    terms: definition,
    killedArchetypes: [],
    runId,
    contractId,
    sectorId: definition.sectorId,
    variant: contractId === "intro" ? "standard" : missionVariant(runId),
    stage: "outbound",
    progress: 0,
    goal: definition.goal,
    cargoHeld: 0,
    outcome: "active",
    rescue: "none",
    difficulty: actualDifficulty,
    eliteKills: 0,
    surveyedSites: [],
    escort: null,
  };
}

export function passiveMaxEnergy(
  passiveId: PassiveId,
  tier: EquipmentTier = 0,
): number {
  return 100 + (passiveId === "capacitor" ? 25 * equipmentTierFactor(tier) : 0);
}
export function passiveWeaponRange(
  range: number,
  passiveId: PassiveId,
  tier: EquipmentTier = 0,
): number {
  return (
    range *
    (passiveId === "targeting" ? 1 + 0.15 * equipmentTierFactor(tier) : 1)
  );
}
export function passiveBoostSpeedFactor(
  passiveId: PassiveId,
  tier: EquipmentTier = 0,
): number {
  return passiveId === "thrusters" ? 1 + 0.15 * equipmentTierFactor(tier) : 1;
}
export function passiveHealingPerSecond(
  passiveId: PassiveId,
  tier: EquipmentTier = 0,
): number {
  return passiveId === "repair-drone" ? equipmentTierFactor(tier) : 0;
}
